import React, { useState, useMemo } from 'react';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import {
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFinancialBookings } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const RevenueTab = () => {
  // Date range - default to current year
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 15;
  
  // Get currency from org
  // Currency from the operator's country (single source of truth)
  const { currencyCode: currency } = useOperatorLocale();
  
  // Fetch via shared financial hook — same canonical cache key as P&L, so the
  // two tabs dedupe to ONE network request and can never diverge.
  const { raw: bookingsData, summary, isLoading, isError: error } = useFinancialBookings(
    dateRange.startDate,
    dateRange.endDate
  );
  
  // Filter to only show paid/partial/refunded bookings (revenue-generating statuses)
  const allBookings = bookingsData?.bookings || [];
  const bookings = allBookings.filter(b => 
    ['paid', 'partial', 'refunded', 'completed', 'confirmed'].includes(b.paymentStatus || b.payment_status || b.status)
  );
  
  // 🔒 SSOT: money total comes ONLY from the backend-computed summary
  // (financial_utils). We never recompute it client-side — count/avg are
  // display-only derivations, not money.
  const totals = useMemo(() => {
    const count = bookings.filter(b => {
      const status = b.paymentStatus || b.payment_status || b.status;
      return status !== 'refunded';
    }).length;
    const total = summary
      ? (summary.totalOwnerRevenue ?? summary.totalRevenue ?? 0)
      : 0;
    const avgBooking = count > 0 ? total / count : 0;
    return { total, count, avgBooking };
  }, [bookings, summary]);
  
  // Pagination
  const totalPages = Math.ceil(bookings.length / ITEMS_PER_PAGE);
  const paginatedBookings = bookings.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  
  // Export functions
  const exportCSV = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Customer', 'Service', 'Revenue', 'Status'];
    const rows = bookings.map(b => [
      b.bookingDate || b.booking_date || '',
      (b.customerName || b.customer_name || 'N/A').replace(/,/g, ' '),
      (b.serviceName || b.service_name || 'N/A').replace(/,/g, ' '),
      b.ownerRevenue || b.totalAmount || b.total_amount || 0,
      b.paymentStatus || b.payment_status || b.status || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('Revenue exported as CSV!');
  };
  
  const exportJSON = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: { totalRevenue: totals.total, bookingCount: totals.count },
      bookings: bookings.map(b => ({
        date: b.bookingDate || b.booking_date,
        customer: b.customerName || b.customer_name,
        service: b.serviceName || b.service_name,
        revenue: b.ownerRevenue || b.totalAmount || b.total_amount || 0,
        status: b.paymentStatus || b.payment_status || b.status
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('Revenue exported as JSON!');
  };
  
  const exportPDF = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Revenue Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
          .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f9fafb; text-align: left; padding: 12px; border-bottom: 2px solid #e5e7eb; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          .revenue { color: #10b981; text-align: right; }
          .status { padding: 4px 8px; border-radius: 12px; font-size: 12px; }
          .paid { background: #dcfce7; color: #166534; }
          .refunded { background: #f3f4f6; color: #6b7280; }
        </style>
      </head>
      <body>
        <h1>Revenue Report</h1>
        <p>Period: ${dateRange.startDate} to ${dateRange.endDate}</p>
        <div class="summary">
          <strong>Total Revenue:</strong> ${formatCurrency(totals.total, currency)}<br>
          <strong>Bookings:</strong> ${totals.count}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Service</th>
              <th style="text-align:right">Revenue</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${bookings.map(b => {
              const status = b.paymentStatus || b.payment_status || b.status;
              return `
                <tr>
                  <td>${formatDate(b.bookingDate || b.booking_date)}</td>
                  <td>${b.customerName || b.customer_name || '—'}</td>
                  <td>${b.serviceName || b.service_name || '—'}</td>
                  <td class="revenue">${formatCurrency(b.ownerRevenue || b.totalAmount || 0, currency)}</td>
                  <td><span class="status ${status === 'paid' ? 'paid' : 'refunded'}">${status}</span></td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load revenue data');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Revenue" subtitle="Track your income">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  return (
    <TabPageLayout 
      title="Revenue" 
      subtitle="Track your income"
      actions={
        <ExportMenu onExport={(f) => ({ csv: exportCSV, pdf: exportPDF, json: exportJSON }[f]())} />
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker + Total */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <DateRangeControl
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onStartChange={(v) => setDateRange({ ...dateRange, startDate: v })}
            onEndChange={(v) => setDateRange({ ...dateRange, endDate: v })}
          />
          
          {/* Total Revenue Badge */}
          <div className="px-4 py-2 rounded-full bg-green-500/20">
            <span className="text-lg font-medium text-green-600">
              {formatCurrency(totals.total, currency)}
            </span>
            <span className="text-sm ml-2 text-green-600/70">
              Total Revenue
            </span>
          </div>
        </div>
        
        {/* Bookings Table */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
          {bookings.length === 0 ? (
            <EmptyState
              icon={<TrendingUp className="w-10 h-10" />}
              title="No revenue yet"
              description="Paid bookings will appear here"
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedBookings.map((booking) => {
                      const status = booking.paymentStatus || booking.payment_status || booking.status;
                      const isRefunded = status === 'refunded';
                      return (
                        <tr key={booking.id} className={`${isRefunded ? 'opacity-50' : ''} hover:bg-gray-50 transition-colors`}>
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {formatDate(booking.bookingDate || booking.booking_date)}
                          </td>
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {booking.customerName || booking.customer_name || '—'}
                          </td>
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {booking.serviceName || booking.service_name || '—'}
                          </td>
                          <td className={`px-4 py-3 text-right ${isRefunded ? 'text-gray-500 line-through' : 'text-green-600'}`}>
                            {formatCurrency(booking.ownerRevenue || booking.totalAmount || booking.total_amount, currency)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              isRefunded
                                ? 'bg-gray-100 text-gray-500'
                                : status === 'paid' || status === 'completed' || status === 'confirmed'
                                ? 'bg-green-100 text-green-700'
                                : status === 'partial'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, bookings.length)} of {bookings.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-9"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </TabPageLayout>
  );
};

export default RevenueTab;
